export class PushCartDiscount extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.MESSAGES = {
      empty: this.dataset.contentEmpty || 'Please enter a promo code.',
      duplicate:
        this.dataset.contentDuplicate || 'This promo code is already applied.',
      invalid:
        this.dataset.contentInvalid ||
        'Promo code is invalid or not applicable.',
      generic:
        this.dataset.contentGeneric ||
        'Something went wrong. Please try again.',
    }
  }

  setupListeners () {
    super.setupListeners()

    this.itemTemplate = this.querySelector('[data-discount-item-template]')

    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleListClick = this.handleListClick.bind(this)

    this.listeners.add(this.els.form.element, 'submit', this.handleSubmit)
    this.listeners.add(this.els.list.element, 'click', this.handleListClick)

    this.els.list.element.hidden = !this.els.list.element.children.length
  }

  async handleSubmit (e) {
    e.preventDefault()

    const code = this.els.input.element.value.trim()

    if (!code) {
      this.showError(this.MESSAGES.empty)
      return
    }

    this.setLoading(true)
    this.clearError()

    try {
      const existing = await this.getAppliedCodes()

      if (existing.some((c) => this.isSameCode(c, code))) {
        this.showError(this.MESSAGES.duplicate)
        return
      }

      // updateDiscount replaces the code list, so send existing + new
      const state = await window.BAO.Cart.updateDiscount([...existing, code])

      if (!state) {
        this.showError(this.MESSAGES.generic)
        return
      }

      const applied = state.discount_codes.find((d) =>
        this.isSameCode(d.code, code),
      )

      if (!applied?.applicable) {
        // Drop the invalid code so it doesn't linger in the cart
        const reverted = await window.BAO.Cart.updateDiscount(existing)

        this.renderDiscounts(reverted ?? state)
        this.showError(this.MESSAGES.invalid)
        return
      }

      this.els.input.element.value = ''
      this.renderDiscounts(state)
    } catch (err) {
      this.showError(this.MESSAGES.generic)
    } finally {
      this.setLoading(false)
    }
  }

  async handleListClick (e) {
    const button = e.target.closest('[data-discount-remove]')

    if (!button) return

    const code = button.closest('[data-code]').dataset.code

    button.disabled = true
    this.clearError()

    try {
      const existing = await this.getAppliedCodes()
      const remaining = existing.filter((c) => !this.isSameCode(c, code))
      const state = await window.BAO.Cart.updateDiscount(remaining)

      if (!state) {
        button.disabled = false
        this.showError(this.MESSAGES.generic)
        return
      }

      this.renderDiscounts(state)
      this.els.input.element.value = ''
    } catch (err) {
      button.disabled = false
      this.showError(this.MESSAGES.generic)
    }
  }

  async getAppliedCodes () {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    })
    const cart = await response.json()

    return this.getApplicableCodes(cart)
  }

  getApplicableCodes (state) {
    return state.discount_codes.filter((d) => d.applicable).map((d) => d.code)
  }

  isSameCode (a, b) {
    return a.toLowerCase() === b.toLowerCase()
  }

  renderDiscounts (state) {
    const list = this.els.list.element
    const items = this.getApplicableCodes(state).map((code) =>
      this.createItem(code),
    )

    list.replaceChildren(...items)
    list.hidden = !items.length
  }

  createItem (code) {
    const item = this.itemTemplate.content.firstElementChild.cloneNode(true)

    item.dataset.code = code
    item.querySelector('[data-discount-code]').textContent = code
    item
      .querySelector('[data-discount-remove]')
      .setAttribute('aria-label', `Remove promo code ${code}`)

    return item
  }

  setLoading (isLoading) {
    this.els.input.element.disabled = isLoading
    this.els.apply.element.disabled = isLoading
    this.toggleAttribute('loading', isLoading)
  }

  showError (message) {
    this.els.error.element.textContent = message
    this.els.error.element.hidden = false
    this.els.input.element.setAttribute('aria-invalid', 'true')
  }

  clearError () {
    this.els.error.element.textContent = ''
    this.els.error.element.hidden = true
    this.els.input.element.removeAttribute('aria-invalid')
  }
}

if (!customElements.get('push-cart-discount')) {
  customElements.define('push-cart-discount', PushCartDiscount)
}
